// sound5.js: the Starlex v5 "GRAVITY" film score, synthesised live with Web
// Audio (no audio files). The shell codes against this API; every method is
// safe before unlock(), when muted, after dispose() and with junk arguments.
//
//   const score = new Score({ muted, tier?, context?, seed? });
//   score.unlock()            call inside a user gesture (the Score also arms its own
//                             pointerup / touchend / click / keydown listeners and keeps
//                             retrying until audio is really running: iOS and Android
//                             only start audio on those events, not on a touch pointerdown)
//   score.update(dt, state)   every frame; state = { chapter 0..5, progress 0..1,
//                             global 0..1, charge 0..1 (press-and-hold amount),
//                             velocity, igniteT (s since ignition, or -1), brand,
//                             transition 0..1, look {x, y} }
//   score.cue(name)           'ignite' | 'whoosh' | 'horizon' | 'brand' | 'click' | 'hover' | 'poster'
//   score.setMuted(m), score.muted, score.running, score.dispose()
//
// Options: `context` injects an (Offline)AudioContext for tests; `tier`
// ('high' | 'mid' | 'low') overrides the device guess (it sets voice caps and
// the reverb). The score is modular: see sound5/*.js.
import { Engine } from './sound5/engine.js';
import { clamp, num } from './sound5/util.js';

const CUES = { ignite: 1, whoosh: 1, horizon: 1, brand: 1, click: 1, hover: 1, poster: 1 };
const GESTURES = ['pointerup', 'touchend', 'click', 'keydown', 'mousedown'];
const hasWin = typeof window !== 'undefined';
// true inside a user activation (or where the browser cannot tell us)
const activated = () => { try { const u = navigator.userActivation; return !u || u.isActive; } catch (e) { return true; } };
const nowMs = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

export function guessTier() {
  if (!hasWin) return 'high';
  try {
    const n = navigator, ua = n.userAgent || '';
    const touch = /iPhone|iPad|iPod|Android|Mobile/i.test(ua) || (n.maxTouchPoints > 1 && /Macintosh/.test(ua)) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    const cores = n.hardwareConcurrency || 4, mem = n.deviceMemory || 4;
    if (touch) return cores <= 4 || mem <= 2 ? 'low' : 'mid';
    return cores <= 2 || mem <= 2 ? 'low' : cores <= 4 ? 'mid' : 'high';
  } catch (e) { return 'mid'; }
}

function cleanState(st, prev) {
  const s = st && typeof st === 'object' ? st : {};
  const look = s.look && typeof s.look === 'object' ? s.look : {};
  return {
    chapter: clamp(Math.round(num(s.chapter, prev ? prev.chapter : 0)), 0, 5),
    progress: clamp(num(s.progress, 0)),
    global: clamp(num(s.global, 0)),
    charge: clamp(num(s.charge, 0)),
    velocity: clamp(num(s.velocity, 0), -50, 50),
    igniteT: num(s.igniteT, -1),
    brand: typeof s.brand === 'string' && s.brand.trim() ? s.brand.slice(0, 40) : prev ? prev.brand : 'Starlex',
    transition: clamp(num(s.transition, 0)),
    look: { x: clamp(num(look.x, 0), -1, 1), y: clamp(num(look.y, 0), -1, 1) },
  };
}

export class Score {
  constructor(opts = {}) {
    const o = opts && typeof opts === 'object' ? opts : {};
    this._muted = !!o.muted;
    this._tier = o.tier || guessTier();
    this._seed = num(o.seed, 1977);
    this._ctx = null;
    this._own = !o.context; // we created the context (and may suspend / close it)
    this._eng = null;
    this._dead = false;
    this._state = cleanState(null, null);
    this._pendingIgnite = -1; // ms timestamp of an 'ignite' cue we could not play yet
    this._armed = false;
    this._suspendT = 0;
    this._errs = 0;
    this._kick = null;
    this._hidden = hasWin && typeof document !== 'undefined' && document.visibilityState === 'hidden';
    this._onVis = () => this._visibility();
    this._onState = () => this._stateChange();
    if (o.context) {
      this._ctx = o.context;
      this._offline = typeof OfflineAudioContext !== 'undefined' && o.context instanceof OfflineAudioContext;
      this._build();
    }
    if (hasWin && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._onVis);
      window.addEventListener('pageshow', this._onVis);
      window.addEventListener('focus', this._onVis);
    }
  }

  get muted() { return this._muted; }
  get running() { return !!(this._eng && !this._muted && !this._dead && this._live()); }
  get tier() { return this._tier; }
  get context() { return this._ctx; }
  // for QA: active voices, the scene, the context state
  get debug() {
    const c = this._ctx;
    return { tier: this._tier, state: c ? c.state : 'none', sampleRate: c ? c.sampleRate : 0, muted: this._muted,
      stats: this._eng ? this._eng.stats() : null, errors: this._errs };
  }

  // Call from inside a user gesture. Resolves once the attempt has been made;
  // if the platform refused, the Score keeps retrying on the next gesture.
  async unlock() {
    if (this._dead) return false;
    this._arm();
    if (hasWin && !activated() && !(this._ctx && this._everRan)) return false; // e.g. a touch pointerdown: wait for pointerup
    this._tryStart(true);
    if (this._ctx && this._own && this._ctx.state !== 'running' && !this._muted) {
      try { await Promise.race([this._ctx.resume(), new Promise((r) => setTimeout(r, 400))]); } catch (e) { /* retry on the next gesture */ }
    }
    return this.running;
  }

  setMuted(m) {
    m = !!m;
    if (this._dead) { this._muted = m; return; }
    this._muted = m;
    if (!m) {
      this._session();
      if ((!this._ctx || !this._live()) && hasWin && activated()) this._tryStart(true);
    }
    this._apply();
  }

  update(dt, state) {
    if (this._dead) return;
    const st = (this._state = cleanState(state, this._state));
    const e = this._eng;
    if (!e) return;
    if (!this._live() || this._muted) {
      // not audible: keep the story straight so unmuting later sounds right
      if (st.igniteT >= 0 && !e.lit && this._pendingIgnite < 0) e.lit = true;
      return;
    }
    this._firePending();
    this._guard(() => e.update(clamp(num(dt, 0.016), 0, 0.25), st));
  }

  cue(name) {
    if (this._dead || typeof name !== 'string' || !CUES[name]) return;
    const e = this._eng;
    if (!e || !this._live() || this._muted) {
      if (name === 'ignite') {
        // phones: the first hold often ends in ignition before any gesture could
        // start audio; play the hit when audio starts (if that is soon)
        if (!this._muted) this._pendingIgnite = nowMs();
        else if (e) e.lit = true;
      } else if (name === 'brand' && e) e.brandAt = -2;
      return;
    }
    this._guard(() => e.cue(name, this._state));
  }

  /* ---------- internals ---------- */

  _live() {
    const c = this._ctx;
    return !!c && (this._offline || c.state === 'running');
  }

  _create() {
    if (!hasWin) return false;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    let c = null;
    // phones: a larger buffer ('balanced') costs a little latency but avoids crackle
    const hint = this._tier === 'high' ? 'interactive' : 'balanced';
    try { c = new AC({ latencyHint: hint }); } catch (e) { try { c = new AC(); } catch (e2) { return false; } }
    this._ctx = c;
    this._offline = false;
    this._build();
    return !!this._eng;
  }

  _build() {
    const c = this._ctx;
    try {
      this._eng = new Engine(c, { tier: this._tier, seed: this._seed });
    } catch (e) {
      this._eng = null;
      this._errs++;
      console.warn('[sound5] disabled:', e);
      return;
    }
    const st = this._state;
    if (st.igniteT >= 0 || st.chapter > 0) this._eng.lit = true; // started mid-film
    try {
      if (c.addEventListener) c.addEventListener('statechange', this._onState);
      else c.onstatechange = this._onState;
    } catch (e) { /* ignore */ }
    this._apply();
  }

  // Safari 17+: play through the ring/silent switch once the visitor turned sound on.
  _session() {
    try { if (hasWin && navigator.audioSession && navigator.audioSession.type !== 'playback') navigator.audioSession.type = 'playback'; } catch (e) { /* unsupported */ }
  }

  // Create / resume the context. `gesture`: we are inside a user gesture, so
  // also play a one-sample silent buffer (the old iOS unlock).
  _tryStart(gesture) {
    if (this._dead) return;
    this._arm();
    if (this._muted || this._hidden) return;
    if (!this._ctx && !this._create()) return;
    if (!this._own) return;
    const c = this._ctx;
    this._session();
    if (c.state !== 'running' && c.state !== 'closed') {
      this._byUs = false;
      try { const p = c.resume(); if (p && p.catch) p.catch(() => {}); } catch (e) { /* retry on the next gesture */ }
      if (gesture) {
        try {
          const s = c.createBufferSource();
          s.buffer = c.createBuffer(1, 1, 22050);
          s.connect(c.destination);
          s.start(0);
        } catch (e) { /* ignore */ }
      }
    }
  }

  // Gesture listeners stay attached (they cost one check per tap) so audio
  // comes back after an iOS 'interrupted' state or an Android focus loss.
  _arm() {
    if (this._armed || !hasWin) return;
    this._armed = true;
    this._kick = (e) => {
      if (this._dead || this._muted || this.running) return;
      if (e && e.type === 'pointerdown' && e.pointerType !== 'mouse') return; // not an activation on touch
      if (!activated()) return; // e.g. Escape, or a scroll that ended a touch
      this._tryStart(true);
    };
    for (const n of GESTURES.concat('pointerdown')) window.addEventListener(n, this._kick, { capture: true, passive: true });
  }

  _disarm() {
    if (!this._armed || !hasWin) return;
    this._armed = false;
    for (const n of GESTURES.concat('pointerdown')) window.removeEventListener(n, this._kick, { capture: true, passive: true });
  }

  _visibility() {
    if (this._dead || typeof document === 'undefined') return;
    const h = document.visibilityState === 'hidden';
    if (h !== this._hidden) { this._hidden = h; this._apply(); }
    else if (!h) this._apply(); // pageshow / focus: resume if the OS suspended us
  }

  // The context changed state behind our back (iOS 'interrupted' by a call or
  // Siri, Android audio focus): try to resume once it is allowed again; the
  // armed gesture listeners cover the cases where only a tap can.
  _stateChange() {
    const c = this._ctx;
    if (c && c.state === 'running') this._everRan = true;
    if (!c || this._dead || !this._own || this._byUs) return;
    if (c.state === 'running' || c.state === 'closed' || this._muted || this._hidden) return;
    const t = nowMs();
    if (t - (this._lastResume || 0) < 1000) return;
    this._lastResume = t;
    setTimeout(() => { if (!this._dead && !this._muted && !this._hidden && c.state !== 'running' && c.state !== 'closed') c.resume().catch(() => {}); }, 250);
  }

  // Fade the output in or out, then suspend our own context while silent
  // (phones keep cool; nothing renders while muted or hidden).
  _apply() {
    const e = this._eng, c = this._ctx;
    if (!e || !c) return;
    const on = !this._muted && (!this._hidden || this._offline) && !this._broken;
    const t = c.currentTime;
    clearTimeout(this._suspendT);
    e.master.kOut.go(on ? 1 : 0, t, on ? 0.35 : 0.06);
    if (!this._own) return;
    if (on) {
      if (c.state !== 'running' && c.state !== 'closed' && (this._everRan || activated())) { this._byUs = false; c.resume().catch(() => {}); }
    } else if (c.state === 'running') {
      this._suspendT = setTimeout(() => {
        if (this._dead || c.state !== 'running' || (!this._muted && !this._hidden && !this._broken)) return;
        this._byUs = true;
        c.suspend().catch(() => {});
      }, 450);
    }
  }

  _firePending() {
    if (this._pendingIgnite < 0) return;
    const age = nowMs() - this._pendingIgnite, e = this._eng;
    this._pendingIgnite = -1;
    if (age < 1200 && this._state.chapter === 0) this._guard(() => e.cue('ignite', this._state));
    else e.lit = true; // too late for the hit: just bloom
  }

  _guard(fn) {
    try { fn(); } catch (e) {
      this._errs++;
      this._lastError = String((e && e.stack) || e).slice(0, 300);
      if (this._errs <= 3) console.warn('[sound5]', e);
      if (this._errs > 30 && !this._broken) { this._broken = true; this._apply(); }
    }
  }

  dispose() {
    if (this._dead) return;
    this._dead = true;
    this._disarm();
    if (hasWin && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVis);
      window.removeEventListener('pageshow', this._onVis);
      window.removeEventListener('focus', this._onVis);
    }
    clearTimeout(this._suspendT);
    const c = this._ctx;
    if (c) {
      try { c.removeEventListener && c.removeEventListener('statechange', this._onState); } catch (e) { /* ignore */ }
      if (this._eng) try { this._eng.master.dispose(); } catch (e) { /* ignore */ }
      if (this._own && c.close) c.close().catch(() => {});
    }
    this._eng = null;
  }
}
